import { useEffect, useState } from "react";
import api, { fmtBRL, fmtInt, fmtNum, fmtPct } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_DAYS = 7;
function isoDate(d) { return d.toISOString().slice(0, 10); }

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [period, setPeriod] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - DEFAULT_DAYS);
    return { start: isoDate(start), end: isoDate(end) };
  });
  const [tab, setTab] = useState("ingredients"); // ingredients | dishes | unmatched

  const load = (p = period) => {
    setLoading(true);
    api.get("/reconciliation", { params: { start_date: p.start, end_date: p.end } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(period); /* eslint-disable-next-line */ }, []);

  const syncSales = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/sync/pdv/sales", { start_date: period.start, end_date: period.end });
      toast.success(`PDV: ${r.data.inserted} novos + ${r.data.updated} atualizados (R$ ${(r.data.total_value || 0).toLocaleString("pt-BR")})`);
      load(period);
    } catch (e) {
      toast.error(`Erro: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !data) return <div className="font-body" style={{ color: "var(--ifl-muted)" }}>Carregando…</div>;
  if (!data) return null;
  const { summary, ingredients, dishes_sold, unmatched_dish_codes } = data;
  const ingredientsWithConsumption = ingredients.filter(i => i.has_consumption);

  return (
    <div data-testid="reconciliation-page">
      <PageHeader
        eyebrow="Conciliação"
        title="TEÓRICO VS REAL"
        accent="onde mora a perda."
        description="Cruzamento real: itens vendidos (PDV) × fichas técnicas (Omie) = consumo teórico por insumo. Compare com a posição do estoque pra detectar perdas, desvios e ruptura."
        actions={
          <>
            <input type="date" value={period.start} onChange={(e) => setPeriod(p => ({ ...p, start: e.target.value }))} className="ifl-input" data-testid="recon-start-date" />
            <input type="date" value={period.end} onChange={(e) => setPeriod(p => ({ ...p, end: e.target.value }))} className="ifl-input" data-testid="recon-end-date" />
            <button onClick={() => load(period)} className="ifl-btn-outline" data-testid="recon-apply">Aplicar</button>
            <button onClick={syncSales} className="ifl-btn" disabled={syncing} data-testid="recon-sync-sales">
              <RefreshCw size={14} strokeWidth={2} className={syncing ? "ifl-pulse" : ""} />
              {syncing ? "Sincronizando…" : "Sync PDV"}
            </button>
          </>
        }
        testId="reconciliation-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="ifl-card p-5">
          <div className="label-caps">Consumo teórico</div>
          <div className="font-display text-2xl mt-2">{fmtBRL(summary.theoretical_consumption_value)}</div>
          <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>
            baseado em vendas × ficha técnica
          </div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Receita de pratos</div>
          <div className="font-display text-2xl mt-2">{fmtBRL(summary.dishes_total_revenue)}</div>
          <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>
            CMV teórico: {summary.dishes_total_revenue ? fmtPct(summary.theoretical_consumption_value / summary.dishes_total_revenue * 100) : "—"}
          </div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Cobertura de ficha</div>
          <div className="font-display text-2xl mt-2" style={{ color: summary.match_rate_pct > 80 ? "var(--ifl-success)" : "var(--ifl-mustard)" }}>
            {fmtPct(summary.match_rate_pct)}
          </div>
          <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>
            {fmtInt(summary.unmatched_dish_count)} pratos sem ficha
          </div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Estoque negativo</div>
          <div className="font-display text-2xl mt-2" style={{ color: "var(--ifl-danger)" }}>{fmtBRL(summary.negative_stock_value)}</div>
          <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>
            {fmtInt(summary.negative_items)} itens negativos · {fmtInt(summary.zero_items)} zerados
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { v: "ingredients", l: `Insumos (${fmtInt(ingredientsWithConsumption.length)})` },
          { v: "dishes", l: `Pratos vendidos (${fmtInt(dishes_sold.length)})` },
          { v: "unmatched", l: `Sem ficha (${fmtInt(summary.unmatched_dish_count)})` },
        ].map(({ v, l }) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={tab === v ? "ifl-btn" : "ifl-btn-outline"}
            data-testid={`recon-tab-${v}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "ingredients" && (
        <div className="ifl-card overflow-hidden">
          <div className="px-6 py-4 border-b" style={{ borderColor: "var(--ifl-border)" }}>
            <div className="label-caps">Consumo teórico por insumo</div>
            <div className="font-italic-serif text-xl mt-1">quanto deveria ter saído.</div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 700 }}>
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Insumo</th>
                  <th>Un.</th>
                  <th className="text-right">Cons. teórico</th>
                  <th className="text-right">Custo teórico</th>
                  <th className="text-right">Saldo estoque</th>
                  <th className="text-right">CMC unit.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ingredientsWithConsumption.map((i, idx) => {
                  const s = i.status === "negative" ? { c: "var(--ifl-danger)", l: "Perda" }
                    : i.status === "zero" ? { c: "var(--ifl-mustard)", l: "Zerado" }
                    : i.status === "no_stock_record" ? { c: "var(--ifl-muted)", l: "Sem registro" }
                    : { c: "var(--ifl-success)", l: "OK" };
                  return (
                    <tr key={idx}>
                      <td className="font-display text-xs">{i.code}</td>
                      <td>{i.name}</td>
                      <td className="font-display text-xs uppercase" style={{ color: "var(--ifl-muted)" }}>{i.unit}</td>
                      <td className="text-right font-display">{fmtNum(i.theoretical_consumption_qty, 2)}</td>
                      <td className="text-right font-display">{fmtBRL(i.theoretical_consumption_value)}</td>
                      <td className="text-right" style={{ color: i.quantity < 0 ? "var(--ifl-danger)" : "var(--ifl-text)" }}>
                        {fmtNum(i.quantity, i.quantity % 1 === 0 ? 0 : 2)}
                      </td>
                      <td className="text-right">{fmtBRL(i.unit_cost)}</td>
                      <td>
                        <span className="inline-flex items-center gap-2">
                          <span className="ifl-status-dot" style={{ background: s.c }} />
                          <span className="font-display text-xs uppercase tracking-widest">{s.l}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {ingredientsWithConsumption.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>
                    Nenhum consumo calculado. Verifique se há vendas sincronizadas no período e fichas cadastradas.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "dishes" && (
        <div className="ifl-card overflow-hidden">
          <div className="px-6 py-4 border-b" style={{ borderColor: "var(--ifl-border)" }}>
            <div className="label-caps">Pratos vendidos no período</div>
            <div className="font-italic-serif text-xl mt-1">o que saiu do salão.</div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 700 }}>
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Cód. PDV</th>
                  <th>Prato</th>
                  <th>Ficha</th>
                  <th className="text-right">Qtde</th>
                  <th className="text-right">Cupons</th>
                  <th className="text-right">Receita</th>
                  <th className="text-right">CMV ficha</th>
                </tr>
              </thead>
              <tbody>
                {dishes_sold.map((d, idx) => (
                  <tr key={idx}>
                    <td className="font-display text-xs">{d.pdv_code}</td>
                    <td>{d.name}</td>
                    <td className="text-xs">
                      {d.matched_recipe ? (
                        <span style={{ color: "var(--ifl-success)" }}>{d.matched_recipe.code} — {d.matched_recipe.name}</span>
                      ) : (
                        <span style={{ color: "var(--ifl-danger)" }}>Sem ficha</span>
                      )}
                    </td>
                    <td className="text-right font-display">{fmtNum(d.qty_sold, 0)}</td>
                    <td className="text-right">{fmtInt(d.tickets)}</td>
                    <td className="text-right font-display">{fmtBRL(d.value)}</td>
                    <td className="text-right" style={{ color: "var(--ifl-muted)" }}>
                      {d.matched_recipe?.cmv_per_unit ? fmtBRL(d.matched_recipe.cmv_per_unit) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "unmatched" && (
        <div className="ifl-card overflow-hidden">
          <div className="px-6 py-4 border-b flex items-start gap-3" style={{ borderColor: "var(--ifl-border)" }}>
            <AlertCircle size={18} strokeWidth={1.5} style={{ color: "var(--ifl-mustard)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <div className="label-caps">Pratos sem ficha técnica</div>
              <div className="font-italic-serif text-xl mt-1">o que está fora do radar de CMV.</div>
              <div className="font-body text-sm mt-2" style={{ color: "var(--ifl-muted)" }}>
                Esses pratos foram vendidos no PDV mas não têm ficha técnica cadastrada no Omie (ou o código de integração não está vinculado). Sem ficha, não conseguimos calcular consumo teórico de insumos para esses itens.
              </div>
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 700 }}>
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Cód. PDV (codProdutoExterno)</th>
                  <th>Prato</th>
                  <th className="text-right">Qtde vendida</th>
                  <th className="text-right">Receita</th>
                </tr>
              </thead>
              <tbody>
                {unmatched_dish_codes.map((u, idx) => (
                  <tr key={idx}>
                    <td className="font-display text-xs">{u.pdv_code}</td>
                    <td>{u.name}</td>
                    <td className="text-right">{fmtNum(u.qty_sold, 0)}</td>
                    <td className="text-right font-display">{fmtBRL(u.value)}</td>
                  </tr>
                ))}
                {unmatched_dish_codes.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-12" style={{ color: "var(--ifl-success)" }}>Tudo está mapeado 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
